import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
  type ReactNode,
} from "react";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { errorToast } from "@telluscompany/design-system";

import { useLocalization } from "@i18n";
import { bignumberCardStyle } from "@shared/utils";

import type { AdminStructureDamRegionalClassification } from "../../api/structures/structures.types";
import { useAdminStructureRegionalClassificationQuery } from "../../api/structures/structures.queries";
import { usePatchAdminStructureRegionalClassificationMutation } from "../../api/structures/structures.mutations";
import { STRUCTURE_REGIONAL_CLASSIFICATION_FORM_ID } from "./structure-modal-form-ids";
import {
  CLASSIFICATION_TAB_ROOT,
  ctd,
  classificationTabIconSx,
} from "./classification-tab-dark-mode";

import {
  buildRegionalNorm,
  classifyCri,
  classifySecondSection,
  getAllRegionalColumnKeys,
  getRegionalEcColumnKeys,
  getRegionalNormsForState,
  getRegionalStates,
} from "../../catalog/build-regional-sections";
import type {
  CardOption,
  ClassificationLevel,
  ColumnDef,
  GradeDef,
  RegionalNorm,
  RegionalNormBands,
  RegionalNormDef,
  RegionalState,
  SectionDef,
} from "../../catalog/regional-classification.catalog.types";

const PREFIX = "admin.structures.regionalClassification";

const I18N = {
  classification: {
    high: `${PREFIX}.classification.high`,
    medium: `${PREFIX}.classification.medium`,
    low: `${PREFIX}.classification.low`,
  },
  state: {
    label: `${PREFIX}.state.label`,
    placeholder: `${PREFIX}.state.placeholder`,
  },
  norm: {
    label: `${PREFIX}.norm.label`,
    placeholder: `${PREFIX}.norm.placeholder`,
    locked: `${PREFIX}.norm.locked`,
  },
  label: {
    criBignumberTitle: `${PREFIX}.label.criBignumberTitle`,
    secondBignumberTitle: `${PREFIX}.label.secondBignumberTitle`,
    referenceDocButton: `${PREFIX}.label.referenceDocButton`,
    referenceDocPanel: `${PREFIX}.label.referenceDocPanel`,
    close: `${PREFIX}.label.close`,
    total: `${PREFIX}.label.total`,
    classificationStatus: `${PREFIX}.label.classificationStatus`,
    partial: `${PREFIX}.label.partial`,
    points: `${PREFIX}.label.points`,
    classification: `${PREFIX}.label.classification`,
    riskCategory: `${PREFIX}.label.riskCategory`,
    riskCategoryCri: `${PREFIX}.label.riskCategoryCri`,
  },
  criPanel: {
    sectionRowNumber: `${PREFIX}.criPanel.sectionRowNumber`,
    gradeRowNumber: `${PREFIX}.criPanel.gradeRowNumber`,
    totalFormula: `${PREFIX}.criPanel.totalFormula`,
    bandsTitle: `${PREFIX}.criPanel.bandsTitle`,
    bandHigh: `${PREFIX}.criPanel.bandHigh`,
    bandMedium: `${PREFIX}.criPanel.bandMedium`,
    bandLow: `${PREFIX}.criPanel.bandLow`,
    footnote: `${PREFIX}.criPanel.footnote`,
  },
  secondPanel: {
    sectionRowNumber: `${PREFIX}.secondPanel.sectionRowNumber`,
    criterionRowNumber: `${PREFIX}.secondPanel.criterionRowNumber`,
    damageCategory: `${PREFIX}.secondPanel.damageCategory`,
    totalLabel: `${PREFIX}.secondPanel.totalLabel`,
    damageClassification: `${PREFIX}.secondPanel.damageClassification`,
    bandsTitle: `${PREFIX}.secondPanel.bandsTitle`,
    bandHigh: `${PREFIX}.secondPanel.bandHigh`,
    bandMedium: `${PREFIX}.secondPanel.bandMedium`,
    bandLow: `${PREFIX}.secondPanel.bandLow`,
    matrixFooter: `${PREFIX}.secondPanel.matrixFooter`,
  },
  validation: {
    incompleteSelectionTitle: `${PREFIX}.validation.incompleteSelectionTitle`,
    incompleteSelectionDescription: `${PREFIX}.validation.incompleteSelectionDescription`,
    normRequired: `${PREFIX}.validation.normRequired`,
  },
  catalogNorm: (norm: RegionalNorm) => `${PREFIX}.catalog.norms.${norm}`,
} as const;

export type AdminStructureRegionalClassificationExternalClassification = {
  data: AdminStructureDamRegionalClassification | null | undefined;
  isLoading?: boolean;
};

export type AdminStructureRegionalClassificationTabProps = {
  complexId?: string;
  structureId: string;
  canWrite: boolean;
  onSaved?: (classification: AdminStructureDamRegionalClassification) => void;
  onSavingChange?: (isSaving: boolean) => void;
  /** General read-only: inject GET payload and skip admin API. */
  externalClassification?: AdminStructureRegionalClassificationExternalClassification;
};

type Selections = Record<string, number | undefined>;

const sumColumns = (selections: Selections, columns: ColumnDef[]): number =>
  columns.reduce((acc, col) => acc + (selections[col.key] ?? 0), 0);

const classificationLabel = (
  level: ClassificationLevel,
  translate: (key: string) => string,
): string => translate(I18N.classification[level]);

const formatCardScore = (score: number): string => score.toString().padStart(2, "0");

const formatPanelPoints = (value: number | undefined): string =>
  value === undefined ? "00" : String(value);

/** Remove prefixo numérico do subtítulo de seção (ex.: "2 - Potencial…" → "Potencial…"). */
const sectionDocTableTitle = (subtitle: string): string =>
  subtitle.replace(/^\d+\s*-\s*/, "");

const MATRIX_MAX_VISIBLE_COLUMNS = 5;
const MATRIX_COLUMN_GAP_PX = 8;

const calcMatrixColumnWidth = (containerWidth: number, columnCount: number): number => {
  if (containerWidth <= 0) return 0;

  const fiveColumnWidth =
    (containerWidth - (MATRIX_MAX_VISIBLE_COLUMNS - 1) * MATRIX_COLUMN_GAP_PX) /
    MATRIX_MAX_VISIBLE_COLUMNS;

  if (columnCount <= MATRIX_MAX_VISIBLE_COLUMNS) {
    return (containerWidth - (columnCount - 1) * MATRIX_COLUMN_GAP_PX) / columnCount;
  }

  return fiveColumnWidth;
};

const useMatrixColumnWidth = (columnCount: number, enabled: boolean) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [columnWidthPx, setColumnWidthPx] = useState(0);

  useEffect(() => {
    if (!enabled) return;

    const el = scrollRef.current;
    if (!el) return;

    const measure = () => {
      setColumnWidthPx(calcMatrixColumnWidth(el.clientWidth, columnCount));
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [columnCount, enabled]);

  return {
    scrollRef,
    columnWidthPx,
    needsScroll: columnCount > MATRIX_MAX_VISIBLE_COLUMNS,
  };
};

type OptionCardProps = {
  colKey: string;
  option: CardOption;
  selected: boolean;
  disabled: boolean;
  onChange: (score: number) => void;
};

const OptionCard = ({ colKey, option, selected, disabled, onChange }: OptionCardProps) => (
  <button
    type="button"
    role="radio"
    aria-label={`${colKey} ${option.score}`}
    aria-checked={selected}
    disabled={disabled}
    onClick={() => onChange(option.score)}
    className={[
      "box-border flex h-full min-h-[128px] w-full flex-col items-center justify-between rounded-xl border p-2.5 text-center transition-colors",
      selected
        ? "border-[#1e170d] bg-[#ab9068] text-white"
        : `border-[#d9d9d9] bg-white text-[#4d4d4d] hover:border-[#ab9068]/60 ${ctd.optionIdle}`,
      disabled ? "cursor-default opacity-70" : "cursor-pointer",
    ].join(" ")}
  >
    <span className="flex w-full flex-1 items-center justify-center px-1 text-sm leading-snug">
      {option.text}
    </span>
    <span
      className={[
        "mt-1 shrink-0 text-base font-bold",
        selected ? "text-white" : `text-[#4d4d4d] ${ctd.optionScore}`,
      ].join(" ")}
    >
      {formatCardScore(option.score)}
    </span>
  </button>
);

const ColumnHeaderCell = ({ column }: { column: ColumnDef }) => (
  <div className={`box-border flex h-[76px] min-h-[76px] max-h-[76px] items-center justify-center overflow-hidden rounded-xl border border-[#d9d9d9] bg-[#f2f2f2] p-2.5 text-center ${ctd.surfaceMuted}`}>
    <p className={`line-clamp-3 w-full text-sm font-bold leading-normal text-[#4d4d4d] ${ctd.text}`}>
      {column.title} ({column.letter})
    </p>
  </div>
);

type GradeOptionMatrixProps = {
  grade: GradeDef;
  selections: Selections;
  disabled: boolean;
  columnWidthPx: number;
  needsScroll: boolean;
  onSelect: (key: string, score: number) => void;
};

const GradeOptionMatrix = ({
  grade,
  selections,
  disabled,
  columnWidthPx,
  needsScroll,
  onSelect,
}: GradeOptionMatrixProps) => {
  const columnCount = grade.columns.length;
  const rowCount = Math.max(...grade.columns.map((col) => col.options.length), 0);

  const gridStyle: CSSProperties = {
    gridTemplateColumns:
      columnWidthPx > 0
        ? `repeat(${columnCount}, ${columnWidthPx}px)`
        : `repeat(${columnCount}, minmax(0, 1fr))`,
    ...(needsScroll && columnWidthPx > 0
      ? {
          width: columnCount * columnWidthPx + (columnCount - 1) * MATRIX_COLUMN_GAP_PX,
        }
      : { width: "100%" }),
  };

  return (
    <div className="grid gap-2" style={gridStyle}>
      {grade.columns.map((col) => (
        <ColumnHeaderCell key={`header-${col.key}`} column={col} />
      ))}
      {Array.from({ length: rowCount }, (_, rowIndex) =>
        grade.columns.map((col) => {
          const option = col.options[rowIndex];
          const cellKey = `${col.key}-row-${rowIndex}`;

          if (!option) {
            return <div key={cellKey} aria-hidden className="min-h-0" />;
          }

          return (
            <div key={cellKey} role="radiogroup" aria-label={col.key} className="flex min-h-[128px]">
              <OptionCard
                colKey={col.key}
                option={option}
                selected={selections[col.key] === option.score}
                disabled={disabled}
                onChange={(score) => onSelect(col.key, score)}
              />
            </div>
          );
        }),
      )}
    </div>
  );
};

type SectionBignumberProps = {
  title: string;
  total: number;
  classificationText: string;
  classificationLevel: ClassificationLevel;
  referenceDocAriaLabel: string;
  totalAriaLabel: string;
  classificationAriaLabel: string;
  infoOpen: boolean;
  onToggleInfo: () => void;
};

const SectionBignumber = ({
  title,
  total,
  classificationText,
  classificationLevel,
  referenceDocAriaLabel,
  totalAriaLabel,
  classificationAriaLabel,
  infoOpen,
  onToggleInfo,
}: SectionBignumberProps) => {
  const style = bignumberCardStyle(classificationLevel);

  return (
    <div
      className={[
        "w-full max-w-[344px] rounded-2xl border px-6 py-4 shadow-[1px_2px_4px_rgba(25,25,28,0.08)] transition-colors",
        style.card,
      ].join(" ")}
    >
      <div className="flex items-center justify-between gap-2">
        <p className={["text-lg font-bold leading-snug", style.title].join(" ")}>{title}</p>
        <button
          type="button"
          aria-label={referenceDocAriaLabel}
          aria-expanded={infoOpen}
          onClick={onToggleInfo}
          className={[
            "flex h-12 w-12 shrink-0 cursor-pointer items-center justify-center rounded-lg hover:bg-black/5",
            style.title,
            ctd.hoverSubtle,
          ].join(" ")}
        >
          <InfoOutlinedIcon sx={classificationTabIconSx} />
        </button>
      </div>
      <p
        aria-label={totalAriaLabel}
        className={["mt-2 text-[40px] font-bold leading-normal", style.score].join(" ")}
      >
        {String(total)}
      </p>
      <span
        role="status"
        aria-label={classificationAriaLabel}
        className={["mt-1 block text-base font-medium", style.label].join(" ")}
      >
        {classificationText}
      </span>
    </div>
  );
};

type DocPanelCellProps = {
  children: ReactNode;
  header?: boolean;
  narrow?: boolean;
  wide?: boolean;
  align?: "left" | "center" | "right";
  ariaLabel?: string;
};

const DocPanelCell = ({
  children,
  header = false,
  narrow = false,
  wide = false,
  align = "center",
  ariaLabel,
}: DocPanelCellProps) => (
  <div
    aria-label={ariaLabel}
    className={[
      "flex items-center border border-[#d9d9d9] px-2 py-1 text-sm",
      ctd.border,
      header ? `bg-[#f2f2f2] font-bold text-[#4d4d4d] ${ctd.surfaceMuted} ${ctd.text}` : `bg-white font-normal text-[#4d4d4d] ${ctd.surfaceWhite} ${ctd.text}`,
      narrow ? "w-[65px] shrink-0 justify-center" : wide ? "min-w-0 flex-1" : "w-[275px] shrink-0 justify-center",
      align === "right"
        ? "justify-end text-right"
        : align === "left"
          ? "justify-start text-left"
          : "justify-center text-center",
    ].join(" ")}
  >
    {children}
  </div>
);

type DocPanelTableProps = {
  panelAriaLabel: string;
  panelTitle: string;
  closeLabel: string;
  onClose: () => void;
  children: ReactNode;
};

const DocPanelTable = ({
  panelAriaLabel,
  panelTitle,
  closeLabel,
  onClose,
  children,
}: DocPanelTableProps) => (
  <div
    role="region"
    aria-label={panelAriaLabel}
    className={`overflow-hidden rounded-lg border border-[#d9dee5] bg-[#fafafc] ${ctd.docPanel}`}
  >
    <div className={`flex h-11 items-center justify-between bg-[#f0f2f7] px-4 ${ctd.docHeader}`}>
      <p className={`text-sm font-semibold text-[#26262e] ${ctd.text}`}>{panelTitle}</p>
      <button
        type="button"
        onClick={onClose}
        className="cursor-pointer text-[13px] text-[#3366cc]"
      >
        {closeLabel}
      </button>
    </div>
    <div className={`flex flex-col gap-2 bg-white p-0 ${ctd.docBody}`}>{children}</div>
  </div>
);

type GradeAccordionProps = {
  grade: GradeDef;
  partialValue: number;
  partialAriaLabel: string;
  expanded: boolean;
  disabled: boolean;
  selections: Selections;
  onToggle: () => void;
  onSelect: (key: string, score: number) => void;
};

const GradeAccordion = ({
  grade,
  partialValue,
  partialAriaLabel,
  expanded,
  disabled,
  selections,
  onToggle,
  onSelect,
}: GradeAccordionProps) => {
  const columnCount = grade.columns.length;
  const { scrollRef, columnWidthPx, needsScroll } = useMatrixColumnWidth(columnCount, expanded);

  return (
    <div className="flex w-full flex-col">
      <button
        type="button"
        onClick={onToggle}
        className={`flex h-10 w-full cursor-pointer items-center justify-between rounded-t-xl border border-[#d9d9d9] bg-white px-4 py-2 shadow-[1px_2px_4px_rgba(25,25,28,0.08)] ${ctd.surfaceWhite} ${ctd.text}`}
      >
        <span className={`flex items-center gap-2 text-[#4d4d4d] ${ctd.iconHost}`}>
          <CheckCircleOutlineIcon sx={classificationTabIconSx} />
          <span className="text-sm font-semibold">{grade.fullTitle}</span>
        </span>
        {expanded ? (
          <KeyboardArrowUpIcon sx={classificationTabIconSx} />
        ) : (
          <KeyboardArrowDownIcon sx={classificationTabIconSx} />
        )}
      </button>

      {expanded ? (
        <div className={`flex flex-col gap-4 rounded-b-2xl border border-t-0 border-[#d9d9d9] px-4 pb-4 pt-2 shadow-[1px_2px_8px_rgba(25,25,28,0.08)] ${ctd.gradeBody}`}>
          <input
            type="text"
            aria-label={partialAriaLabel}
            value={String(partialValue)}
            readOnly
            className={`h-10 w-24 rounded-xl border border-[#999] bg-white px-4 text-sm text-[#4d4d4d] ${ctd.surfaceWhite} ${ctd.text}`}
          />
          <div
            ref={scrollRef}
            className={needsScroll ? "overflow-x-auto pb-2" : "overflow-x-hidden pb-2"}
          >
            <GradeOptionMatrix
              grade={grade}
              selections={selections}
              disabled={disabled}
              columnWidthPx={columnWidthPx}
              needsScroll={needsScroll}
              onSelect={onSelect}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
};

type ReferenceDocPanelProps = {
  section: SectionDef;
  total: number;
  level: ClassificationLevel;
  bands: RegionalNormBands["cri"] | RegionalNormBands["second"];
  ecHighScore?: number;
  selections: Selections;
  translate: (key: string, options?: Record<string, unknown>) => string;
  onClose: () => void;
};

const CriReferenceDocPanel = ({
  section,
  total,
  level,
  bands,
  ecHighScore,
  selections,
  translate,
  onClose,
}: ReferenceDocPanelProps & { ecHighScore: number }) => {
  const sectionLabel = section.label;
  const criClassLabel = classificationLabel(level, translate);

  return (
    <DocPanelTable
      panelAriaLabel={translate(I18N.label.referenceDocPanel, { section: sectionLabel })}
      panelTitle={translate(I18N.label.referenceDocPanel, { section: sectionLabel })}
      closeLabel={translate(I18N.label.close)}
      onClose={onClose}
    >
      <div className="p-2">
        <div className={`rounded-xl border border-[#d9d9d9] bg-[#f2f2f2] px-2 py-1 text-center text-sm font-bold text-[#4d4d4d] ${ctd.surfaceMuted} ${ctd.text}`}>
          {translate(I18N.label.riskCategory)}
        </div>

        <div className={`mt-2 overflow-hidden rounded-xl border border-[#d9d9d9] ${ctd.border}`}>
          <div className="flex">
            <DocPanelCell header narrow>
              {translate(I18N.criPanel.sectionRowNumber)}
            </DocPanelCell>
            <DocPanelCell header wide>
              {translate(I18N.label.riskCategoryCri)}
            </DocPanelCell>
            <DocPanelCell header>{translate(I18N.label.points)}</DocPanelCell>
          </div>
          {section.grades.map((grade, index) => (
            <div key={grade.key} className="flex">
              <DocPanelCell header narrow>
                {translate(I18N.criPanel.gradeRowNumber, { index: index + 1 })}
              </DocPanelCell>
              <DocPanelCell wide align="center">
                {grade.fullTitle}
              </DocPanelCell>
              <DocPanelCell
                ariaLabel={translate(I18N.label.partial, { grade: grade.code })}
              >
                {formatPanelPoints(sumColumns(selections, grade.columns))}
              </DocPanelCell>
            </div>
          ))}
          <div className="flex">
            <DocPanelCell wide align="right">
              <span className="font-bold">{translate(I18N.criPanel.totalFormula)}</span>
            </DocPanelCell>
            <DocPanelCell ariaLabel={translate(I18N.label.total, { section: sectionLabel })}>
              <span className="font-bold">{formatPanelPoints(total)}</span>
            </DocPanelCell>
          </div>
          <div className="flex">
            <DocPanelCell wide align="right">
              <span className="font-bold">{translate(I18N.label.riskCategory)}</span>
            </DocPanelCell>
            <DocPanelCell
              ariaLabel={translate(I18N.label.classificationStatus, { section: sectionLabel })}
            >
              <span className="font-bold">{criClassLabel}</span>
            </DocPanelCell>
          </div>
        </div>

        <div className={`mt-2 overflow-hidden rounded-xl border border-[#d9d9d9] ${ctd.border}`}>
          <div className="flex">
            <DocPanelCell header wide>
              {translate(I18N.criPanel.bandsTitle)}
            </DocPanelCell>
            <div className="flex min-w-0 flex-1 flex-col">
              <div className="flex">
                <DocPanelCell header wide>
                  {translate(I18N.label.riskCategory)}
                </DocPanelCell>
                <DocPanelCell header wide>{sectionLabel}</DocPanelCell>
              </div>
              <div className="flex">
                <DocPanelCell wide>{translate(I18N.classification.high)}</DocPanelCell>
                <DocPanelCell wide>
                  {translate(I18N.criPanel.bandHigh, {
                    highMin: bands.highMin,
                    ecScore: ecHighScore,
                  })}
                </DocPanelCell>
              </div>
              <div className="flex">
                <DocPanelCell wide>{translate(I18N.classification.medium)}</DocPanelCell>
                <DocPanelCell wide>
                  {translate(I18N.criPanel.bandMedium, {
                    mediumMin: bands.mediumMin,
                    highMin: bands.highMin,
                  })}
                </DocPanelCell>
              </div>
              <div className="flex">
                <DocPanelCell wide>{translate(I18N.classification.low)}</DocPanelCell>
                <DocPanelCell wide>
                  {translate(I18N.criPanel.bandLow, { mediumMin: bands.mediumMin })}
                </DocPanelCell>
              </div>
            </div>
          </div>
          <div className={`border border-[#d9d9d9] bg-[#f2f2f2] px-2 py-1 text-xs font-medium text-[#4d4d4d] ${ctd.surfaceMuted} ${ctd.text}`}>
            {translate(I18N.criPanel.footnote, { ecScore: ecHighScore })}
          </div>
        </div>
      </div>
    </DocPanelTable>
  );
};

const SecondReferenceDocPanel = ({
  section,
  total,
  level,
  bands,
  selections,
  translate,
  onClose,
}: ReferenceDocPanelProps) => {
  const sectionLabel = section.label;
  const grade = section.grades[0];
  const secondClassLabel = classificationLabel(level, translate);

  if (!grade) return null;

  return (
    <DocPanelTable
      panelAriaLabel={translate(I18N.label.referenceDocPanel, { section: sectionLabel })}
      panelTitle={translate(I18N.label.referenceDocPanel, { section: sectionLabel })}
      closeLabel={translate(I18N.label.close)}
      onClose={onClose}
    >
      <div className="p-2">
        <div className={`rounded-xl border border-[#d9d9d9] bg-[#f2f2f2] px-2 py-1 text-center text-sm font-bold text-[#4d4d4d] ${ctd.surfaceMuted} ${ctd.text}`}>
          {sectionDocTableTitle(section.subtitle)}
        </div>

        <div className={`mt-2 overflow-hidden rounded-xl border border-[#d9d9d9] ${ctd.border}`}>
          <div className="flex">
            <DocPanelCell header narrow>
              {translate(I18N.secondPanel.sectionRowNumber)}
            </DocPanelCell>
            <DocPanelCell header wide>
              {translate(I18N.secondPanel.damageCategory, { section: sectionLabel })}
            </DocPanelCell>
            <DocPanelCell header>{translate(I18N.label.points)}</DocPanelCell>
          </div>
          {grade.columns.map((col, index) => (
            <div key={col.key} className="flex">
              <DocPanelCell header narrow>
                {translate(I18N.secondPanel.criterionRowNumber, { index: index + 1 })}
              </DocPanelCell>
              <DocPanelCell wide align="center">
                {col.title}
              </DocPanelCell>
              <DocPanelCell ariaLabel={translate(I18N.label.partial, { grade: col.title })}>
                {formatPanelPoints(selections[col.key])}
              </DocPanelCell>
            </div>
          ))}
          <div className="flex">
            <DocPanelCell wide align="right">
              <span className="font-bold">
                {translate(I18N.secondPanel.totalLabel, { section: sectionLabel })}
              </span>
            </DocPanelCell>
            <DocPanelCell ariaLabel={translate(I18N.label.total, { section: sectionLabel })}>
              <span className="font-bold">{formatPanelPoints(total)}</span>
            </DocPanelCell>
          </div>
          <div className="flex">
            <DocPanelCell wide align="right">
              <span className="font-bold">{translate(I18N.secondPanel.damageClassification)}</span>
            </DocPanelCell>
            <DocPanelCell
              ariaLabel={translate(I18N.label.classificationStatus, { section: sectionLabel })}
            >
              <span className="font-bold">{secondClassLabel}</span>
            </DocPanelCell>
          </div>
        </div>

        <div className={`mt-2 overflow-hidden rounded-xl border border-[#d9d9d9] ${ctd.border}`}>
          <div className="flex">
            <DocPanelCell header wide>
              {translate(I18N.secondPanel.bandsTitle)}
            </DocPanelCell>
            <div className="flex min-w-0 flex-1 flex-col">
              <div className="flex">
                <DocPanelCell header wide>
                  {translate(I18N.secondPanel.damageCategory, { section: sectionLabel })}
                </DocPanelCell>
                <DocPanelCell header wide>{sectionLabel}</DocPanelCell>
              </div>
              <div className="flex">
                <DocPanelCell wide>{translate(I18N.classification.high)}</DocPanelCell>
                <DocPanelCell wide>
                  {translate(I18N.secondPanel.bandHigh, { highMin: bands.highMin })}
                </DocPanelCell>
              </div>
              <div className="flex">
                <DocPanelCell wide>{translate(I18N.classification.medium)}</DocPanelCell>
                <DocPanelCell wide>
                  {translate(I18N.secondPanel.bandMedium, {
                    mediumMin: bands.mediumMin,
                    highMin: bands.highMin,
                    section: sectionLabel,
                  })}
                </DocPanelCell>
              </div>
              <div className="flex">
                <DocPanelCell wide>{translate(I18N.classification.low)}</DocPanelCell>
                <DocPanelCell wide>
                  {translate(I18N.secondPanel.bandLow, { mediumMin: bands.mediumMin })}
                </DocPanelCell>
              </div>
            </div>
          </div>
          <div className={`border border-[#d9d9d9] bg-[#f2f2f2] px-2 py-1 text-xs font-medium text-[#4d4d4d] ${ctd.surfaceMuted} ${ctd.text}`}>
            {translate(I18N.secondPanel.matrixFooter)}
          </div>
        </div>
      </div>
    </DocPanelTable>
  );
};

const AdminStructureRegionalClassificationTab = ({
  complexId,
  structureId,
  canWrite,
  onSaved,
  onSavingChange,
  externalClassification,
}: AdminStructureRegionalClassificationTabProps) => {
  const { translate } = useLocalization();
  const useExternalClassification = externalClassification !== undefined;
  const { data: adminData } = useAdminStructureRegionalClassificationQuery(
    complexId ?? "",
    structureId,
    { enabled: !useExternalClassification && Boolean(complexId && structureId) },
  );
  const data = useExternalClassification ? externalClassification.data : adminData;
  const mutation = usePatchAdminStructureRegionalClassificationMutation();

  useEffect(() => {
    return () => {
      onSavingChange?.(false);
    };
  }, [onSavingChange]);

  const persistedNorm = data?.norm ?? null;
  const persistedState = data?.state ?? null;
  const locked = persistedNorm !== null;

  const [selectedState, setSelectedState] = useState<RegionalState | "">("");
  const [selectedNorm, setSelectedNorm] = useState<RegionalNorm | null>(null);
  const [selections, setSelections] = useState<Selections>({});
  const [openPanels, setOpenPanels] = useState<Record<string, boolean>>({});
  const [openGrades, setOpenGrades] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (data === undefined) return;
    if (data === null) {
      setSelectedState("");
      setSelectedNorm(null);
      setSelections({});
      return;
    }
    setSelectedState(data.state);
    setSelectedNorm(data.norm);
    setSelections({ ...data.selections });
  }, [data]);

  const effectiveState: RegionalState | "" = persistedState ?? selectedState;
  const effectiveNorm: RegionalNorm | null = persistedNorm ?? selectedNorm;

  const normDef: RegionalNormDef | null = useMemo(
    () => (effectiveNorm ? buildRegionalNorm(translate, effectiveNorm) : null),
    [effectiveNorm, translate],
  );

  useEffect(() => {
    if (!normDef) {
      setOpenGrades({});
      return;
    }
    setOpenGrades(
      Object.fromEntries(
        normDef.sections.flatMap((section) =>
          section.grades.map((grade) => [grade.key, true]),
        ),
      ),
    );
  }, [normDef]);

  const normsForState = effectiveState ? getRegionalNormsForState(effectiveState) : [];

  const criSection = normDef?.sections.find((s) => s.key === "cri") ?? null;
  const secondSection = normDef?.sections.find((s) => s.key !== "cri") ?? null;

  const criColumns = criSection?.grades.flatMap((g) => g.columns) ?? [];
  const secondColumns = secondSection?.grades.flatMap((g) => g.columns) ?? [];

  const criTotal = sumColumns(selections, criColumns);
  const secondTotal = sumColumns(selections, secondColumns);

  const ecScores = effectiveNorm
    ? getRegionalEcColumnKeys(effectiveNorm).map((key) => selections[key] ?? 0)
    : [];

  const criLevel: ClassificationLevel = normDef
    ? classifyCri(criTotal, ecScores, normDef.bands)
    : "low";
  const secondLevel: ClassificationLevel = normDef
    ? classifySecondSection(secondTotal, normDef.bands)
    : "low";

  const criClassLabel = classificationLabel(criLevel, translate);
  const secondClassLabel = classificationLabel(secondLevel, translate);

  const handleSelect = (key: string, score: number) => {
    if (!canWrite) return;
    setSelections((prev) => ({ ...prev, [key]: score }));
  };

  const handleStateChange = (value: string) => {
    setSelectedState(value as RegionalState | "");
    setSelectedNorm(null);
    setSelections({});
  };

  const togglePanel = (key: string) => {
    setOpenPanels((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleGrade = (key: string) => {
    setOpenGrades((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (useExternalClassification || !canWrite || !complexId) {
      return;
    }

    if (!effectiveState || !effectiveNorm) {
      errorToast({
        title: translate(I18N.validation.normRequired),
        description: translate(I18N.validation.normRequired),
      });
      return;
    }

    const columnKeys = getAllRegionalColumnKeys(effectiveNorm);
    const isComplete = columnKeys.every((key) => selections[key] !== undefined);
    if (!isComplete) {
      errorToast({
        title: translate(I18N.validation.incompleteSelectionTitle),
        description: translate(I18N.validation.incompleteSelectionDescription),
      });
      return;
    }

    const payload: AdminStructureDamRegionalClassification = {
      state: effectiveState,
      norm: effectiveNorm,
      selections: Object.fromEntries(columnKeys.map((key) => [key, selections[key] as number])),
    };

    onSavingChange?.(true);
    try {
      const saved = await mutation.mutateAsync({ complexId, structureId, payload });
      onSaved?.(saved);
    } finally {
      onSavingChange?.(false);
    }
  };

  const renderGrades = (grades: GradeDef[]) => (
    <div className="flex flex-col gap-4">
      {grades.map((grade) => (
        <GradeAccordion
          key={grade.key}
          grade={grade}
          partialValue={sumColumns(selections, grade.columns)}
          partialAriaLabel={translate(I18N.label.partial, { grade: grade.code })}
          expanded={openGrades[grade.key] ?? true}
          disabled={!canWrite}
          selections={selections}
          onToggle={() => toggleGrade(grade.key)}
          onSelect={handleSelect}
        />
      ))}
    </div>
  );

  return (
    <form
      id={STRUCTURE_REGIONAL_CLASSIFICATION_FORM_ID}
      noValidate
      onSubmit={(event) => {
        void handleSubmit(event);
      }}
      className={`${CLASSIFICATION_TAB_ROOT} flex flex-col gap-10`}
    >
      <section className="flex flex-col gap-4">
        <div className="flex w-full max-w-[344px] flex-col gap-1">
          <span className="text-sm font-medium text-foreground">{translate(I18N.state.label)}</span>
          <select
            aria-label={translate(I18N.state.label)}
            value={effectiveState}
            disabled={locked || !canWrite}
            onChange={(event) => handleStateChange(event.target.value)}
            className="h-10 rounded-xl border border-border bg-background px-3 text-sm text-foreground shadow-sm disabled:opacity-60"
          >
            <option value="">{translate(I18N.state.placeholder)}</option>
            {getRegionalStates().map((state) => (
              <option key={state} value={state}>
                {translate(`${PREFIX}.state.${state}`)}
              </option>
            ))}
          </select>
        </div>

        {effectiveState && !locked ? (
          <div role="radiogroup" aria-label={translate(I18N.norm.label)} className="flex flex-col gap-2">
            <span className="text-sm font-medium text-foreground">{translate(I18N.norm.label)}</span>
            {normsForState.map((norm) => {
              const label = translate(I18N.catalogNorm(norm));
              const checked = selectedNorm === norm;
              return (
                <button
                  key={norm}
                  type="button"
                  role="radio"
                  aria-label={label}
                  aria-checked={checked}
                  disabled={!canWrite}
                  onClick={() => setSelectedNorm(norm)}
                  className={[
                    "flex items-center gap-2 rounded-xl border px-4 py-2 text-left text-sm transition-colors",
                    checked
                      ? "border-[#1e170d] bg-[#ab9068] text-white"
                      : `border-[#d9d9d9] bg-white text-[#4d4d4d] ${ctd.normIdle}`,
                    canWrite ? "cursor-pointer" : "cursor-default opacity-70",
                  ].join(" ")}
                >
                  <span
                    aria-hidden
                    className={[
                      "flex h-4 w-4 shrink-0 items-center justify-center rounded-full border",
                      checked ? "border-white" : "border-[#999]",
                    ].join(" ")}
                  >
                    {checked ? <span className="h-2 w-2 rounded-full bg-white" /> : null}
                  </span>
                  {label}
                </button>
              );
            })}
          </div>
        ) : null}

        {locked && normDef ? (
          <div className="flex flex-col gap-1">
            <span className="text-sm font-medium text-foreground">{translate(I18N.norm.label)}</span>
            <p className="text-sm text-foreground">{normDef.label}</p>
            <span className="text-xs text-muted-foreground">{translate(I18N.norm.locked)}</span>
          </div>
        ) : null}
      </section>

      {normDef && criSection ? (
        <section className="flex flex-col gap-4">
          <p className="text-base font-normal text-foreground">{criSection.subtitle}</p>
          <h3 className="sr-only">{criSection.label}</h3>

          <SectionBignumber
            title={translate(I18N.label.criBignumberTitle)}
            total={criTotal}
            classificationText={criClassLabel}
            classificationLevel={criLevel}
            referenceDocAriaLabel={translate(I18N.label.referenceDocButton, {
              section: criSection.label,
            })}
            totalAriaLabel={translate(I18N.label.total, { section: criSection.label })}
            classificationAriaLabel={translate(I18N.label.classificationStatus, {
              section: criSection.label,
            })}
            infoOpen={Boolean(openPanels.cri)}
            onToggleInfo={() => togglePanel("cri")}
          />

          {openPanels.cri ? (
            <CriReferenceDocPanel
              section={criSection}
              total={criTotal}
              level={criLevel}
              bands={normDef.bands.cri}
              ecHighScore={normDef.bands.cri.ecHighScore}
              selections={selections}
              translate={translate}
              onClose={() => togglePanel("cri")}
            />
          ) : null}

          {renderGrades(criSection.grades)}
        </section>
      ) : null}

      {normDef && secondSection ? (
        <section className="flex flex-col gap-4">
          <p className="text-base font-normal text-foreground">{secondSection.subtitle}</p>
          <h3 className="sr-only">{secondSection.label}</h3>

          <SectionBignumber
            title={translate(I18N.label.secondBignumberTitle)}
            total={secondTotal}
            classificationText={secondClassLabel}
            classificationLevel={secondLevel}
            referenceDocAriaLabel={translate(I18N.label.referenceDocButton, {
              section: secondSection.label,
            })}
            totalAriaLabel={translate(I18N.label.total, { section: secondSection.label })}
            classificationAriaLabel={translate(I18N.label.classificationStatus, {
              section: secondSection.label,
            })}
            infoOpen={Boolean(openPanels.second)}
            onToggleInfo={() => togglePanel("second")}
          />

          {openPanels.second ? (
            <SecondReferenceDocPanel
              section={secondSection}
              total={secondTotal}
              level={secondLevel}
              bands={normDef.bands.second}
              selections={selections}
              translate={translate}
              onClose={() => togglePanel("second")}
            />
          ) : null}

          {renderGrades(secondSection.grades)}
        </section>
      ) : null}
    </form>
  );
};

export default AdminStructureRegionalClassificationTab;
